import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Link } from 'react-router-dom';
import { History } from 'lucide-react';
import type { ReactNode } from 'react';
import type { EnergyFlowDataPoint } from '../../types';
import { FLOW_COLORS } from '../../utils/chartColors';

interface EnergyFlowChartProps {
  data: EnergyFlowDataPoint[];
  className?: string;
  historyHref?: string;
  /**
   * Calendar day to render (YYYY-MM-DD in `timezone`). Defaults to today, in
   * which case slots after the current half-hour are left empty.
   */
  date?: string;
  /**
   * IANA timezone used to bucket `data` into the day's 48 half-hour slots.
   * Defaults to the browser's zone. Pass the site's zone when `date` came
   * from a site-local rollup so the two agree on where the day starts.
   */
  timezone?: string;
  /** Card heading. Defaults to "Energy Flow Today". */
  title?: string;
  /** Extra controls rendered in the card header (e.g. day paging). */
  headerExtra?: ReactNode;
}

// Generate time slots for the full day from midnight
function generateDaySlots(): { time: string; slotKey: string }[] {
  const slots = [];
  for (let i = 0; i < 48; i++) {
    const hours = Math.floor(i / 2);
    const minutes = (i % 2) * 30;
    const time = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    slots.push({ time, slotKey: time });
  }
  return slots;
}

interface LocalParts {
  dateKey: string; // YYYY-MM-DD
  slotKey: string; // HH:MM floored to the half hour
}

// Resolve a timestamp to its local calendar day and half-hour slot in the
// given zone. Falls back to the browser's zone when `timezone` is missing or
// not recognised by Intl.
function makeLocalParts(timezone?: string): (ts: string | Date) => LocalParts {
  let fmt: Intl.DateTimeFormat | null = null;
  if (timezone) {
    try {
      fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      });
    } catch {
      fmt = null;
    }
  }
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (ts) => {
    const d = ts instanceof Date ? ts : new Date(ts);
    let year: number, month: number, day: number, hours: number, minutes: number;
    if (fmt) {
      const parts: Record<string, number> = {};
      fmt.formatToParts(d).forEach((part) => {
        if (part.type !== 'literal') parts[part.type] = Number.parseInt(part.value, 10);
      });
      year = parts.year;
      month = parts.month;
      day = parts.day;
      hours = parts.hour;
      minutes = parts.minute;
    } else {
      year = d.getFullYear();
      month = d.getMonth() + 1;
      day = d.getDate();
      hours = d.getHours();
      minutes = d.getMinutes();
    }
    minutes = Math.floor(minutes / 30) * 30;
    return {
      dateKey: `${year}-${pad(month)}-${pad(day)}`,
      slotKey: `${pad(hours)}:${pad(minutes)}`,
    };
  };
}

export function EnergyFlowChart({
  data,
  className = '',
  historyHref,
  date,
  timezone,
  title,
  headerExtra,
}: EnergyFlowChartProps) {
  // Generate all 48 time slots for the day
  const daySlots = generateDaySlots();

  const localParts = makeLocalParts(timezone);
  const nowParts = localParts(new Date());
  const dayKey = date ?? nowParts.dateKey;
  const isToday = dayKey === nowParts.dateKey;
  const isPastDay = dayKey < nowParts.dateKey;

  // Create a map of existing data by slot key. The API may return a rolling
  // window spanning more than one local day, but slots are keyed by HH:MM
  // only, so a neighbouring day's bucket at the same wall-clock time would
  // fill any slot the selected day doesn't have a row for. Keep only rows
  // that fall on the selected day.
  const dataBySlot = new Map<string, EnergyFlowDataPoint>();
  data.forEach((point) => {
    const parts = localParts(point.timestamp);
    if (parts.dateKey !== dayKey) return;
    dataBySlot.set(parts.slotKey, point);
  });

  // Get current time slot to show what's "now"
  const currentSlotKey = nowParts.slotKey;
  const emptyLabel = isToday ? 'No data yet' : 'No data';

  // Build chart data with fixed 48 slots
  // Import and consumption are shown as negative (below 0)
  // Production and export are shown as positive (above 0)
  const chartData = daySlots.map(({ time, slotKey }) => {
    const point = dataBySlot.get(slotKey);
    const isPast = isPastDay || (isToday && slotKey <= currentSlotKey);

    if (point && isPast) {
      return {
        time,
        slotKey,
        import: -point.import_wh / 1000, // Convert to kWh, negative
        consumption: -point.consumption_wh / 1000, // Convert to kWh, negative
        production: point.production_wh / 1000, // Convert to kWh, positive
        export: point.export_wh / 1000, // Convert to kWh, positive
        hasData: true,
        emptyLabel,
      };
    }
    return {
      time,
      slotKey,
      import: 0,
      consumption: 0,
      production: 0,
      export: 0,
      hasData: false,
      emptyLabel,
    };
  });

  return (
    <div className={`card p-4 h-full flex flex-col ${className}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h3 className="font-semibold text-gray-900 dark:text-white">
          {title ?? 'Energy Flow Today'}
        </h3>
        <div className="flex items-center gap-3">
          {headerExtra}
          {historyHref && (
            <Link
              to={historyHref}
              className="inline-flex items-center gap-1 text-xs text-brand-orange hover:underline"
              title="View energy history"
            >
              <History className="w-3.5 h-3.5" />
              History
            </Link>
          )}
        </div>
      </div>
      <div className="flex-1 flex items-center">
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData} stackOffset="sign" barCategoryGap="5%">
          <XAxis
            dataKey="time"
            tick={{ fill: '#9CA3AF', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            interval={5} // Show every 6th label (every 3 hours)
          />
          <YAxis
            tick={{ fill: '#9CA3AF', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => `${Math.abs(value).toFixed(1)}`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            formatter={(value) => (
              <span className="text-gray-600 dark:text-gray-300 text-sm capitalize">
                {value.replace('_', ' ')}
              </span>
            )}
          />
          <ReferenceLine y={0} stroke="#4B5563" strokeDasharray="3 3" />

          {/* All bars use same stackId so they align on the 0 line */}
          <Bar
            dataKey="import"
            stackId="energy"
            fill={FLOW_COLORS.import}
            name="Import from Grid"
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="consumption"
            stackId="energy"
            fill={FLOW_COLORS.consumption}
            name="Consumption"
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="production"
            stackId="energy"
            fill={FLOW_COLORS.production}
            name="Production"
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="export"
            stackId="energy"
            fill={FLOW_COLORS.export}
            name="Export to Grid"
            radius={[0, 0, 0, 0]}
          />
        </ComposedChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    payload: {
      hasData: boolean;
      time: string;
      emptyLabel?: string;
    };
  }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  // Check if this slot has data
  const hasData = payload[0]?.payload?.hasData;
  if (!hasData) {
    return (
      <div className="card p-3">
        <p className="font-medium text-gray-900 dark:text-white mb-1">{label}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {payload[0]?.payload?.emptyLabel ?? 'No data yet'}
        </p>
      </div>
    );
  }

  return (
    <div className="card p-3">
      <p className="font-medium text-gray-900 dark:text-white mb-2">{label}</p>
      {payload.map((entry, index) => (
        entry.value !== 0 && (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-600 dark:text-gray-300">{entry.name}:</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {Math.abs(entry.value).toFixed(2)} kWh
            </span>
          </div>
        )
      ))}
    </div>
  );
}
